import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuClock, LuLogIn, LuLogOut, LuCoffee, LuCornerDownRight, LuCheckCircle2,
} from 'react-icons/lu';

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ textAlign: 'center', marginBottom: 28 }}>
      <div style={{ fontSize: 54, fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: 2 }}>
        {now.toLocaleTimeString('pt-BR')}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 4 }}>
        {now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
      </div>
    </div>
  );
}

function fmt(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function diffMin(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 60000);
}

function fmtMin(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}m`;
}

// Determina o próximo passo com base nos campos do registro
function getStep(rec) {
  if (!rec)              return 'clock_in';
  if (!rec.lunch_out)    return 'lunch_out';
  if (!rec.lunch_in)     return 'lunch_in';
  if (!rec.clock_out)    return 'clock_out';
  return 'done';
}

const STEP_CONFIG = {
  clock_in:  { label: 'Registrar Entrada',      icon: <LuLogIn size={20} />, bg: 'var(--accent)', color: 'var(--on-accent)', action: '/time-records/clock-in'  },
  lunch_out: { label: 'Saída para Almoço',       icon: <LuCoffee size={20} />,          bg: 'var(--warning)', color: 'var(--on-accent)',    action: '/time-records/lunch-out'  },
  lunch_in:  { label: 'Retorno do Almoço',       icon: <LuCornerDownRight size={20} />,bg: 'var(--info)', color: 'var(--on-solid)',    action: '/time-records/lunch-in'   },
  clock_out: { label: 'Registrar Saída',         icon: <LuLogOut size={20} />,   bg: 'var(--danger)', color: 'var(--on-solid)',    action: '/time-records/clock-out'  },
};

const STEP_LABEL = {
  clock_in:  '1. Entrada',
  lunch_out: '2. Saída Almoço',
  lunch_in:  '3. Retorno Almoço',
  clock_out: '4. Saída',
  done:      'Encerrado',
};

export default function TimeclockPage({ user }) {
  const employee = user?.employee || null;

  const [record,       setRecord]       = useState(null);
  const [checking,     setChecking]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [message,      setMessage]      = useState(null);
  const [confirmingOut, setConfirmingOut] = useState(false);

  useEffect(() => {
    if (!employee) return;
    let cancelled = false;
    setChecking(true);
    api.get(`/time-records/today/${employee.id}`)
      .then(({ data }) => { if (!cancelled) setRecord(data); })
      .catch(() => { if (!cancelled) setRecord(null); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [employee]);

  const handleClock = async () => {
    if (!employee) return;
    const step = getStep(record);
    const cfg  = STEP_CONFIG[step];
    if (!cfg) return;

    setLoading(true);
    setMessage(null);
    try {
      const { data } = await api.post(cfg.action, { employee_id: employee.id });
      setRecord(data);
      setConfirmingOut(false);
      const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const msgs = {
        clock_in:  `Entrada registrada às ${hora}. Bom trabalho, ${employee.full_name.split(' ')[0]}!`,
        lunch_out: `Bom almoço! Saída às ${hora}.`,
        lunch_in:  `Bem-vindo de volta! Retorno às ${hora}.`,
        clock_out: `Saída registrada às ${hora}. Até logo, ${employee.full_name.split(' ')[0]}!`,
      };
      setMessage({ type: 'success', text: msgs[step] });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao registrar ponto' });
    } finally {
      setLoading(false);
    }
  };

  if (!employee) {
    return (
      <div className="page-card" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center', padding: 48 }}>
        <LuClock size={40} color="var(--text-3)" style={{ marginBottom: 14 }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
          Usuário sem funcionário vinculado
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
          Solicite ao administrador que vincule sua conta em <strong>Sistema → Usuários</strong>.
        </div>
      </div>
    );
  }

  const step = getStep(record);
  const cfg  = STEP_CONFIG[step];

  // Calcula horas trabalhadas até agora (se parcial) ou total (se encerrado)
  const workedNow = () => {
    if (!record?.clock_in) return null;
    const ref = record.clock_out ? new Date(record.clock_out) : new Date();
    const lunchDur = (record.lunch_out && record.lunch_in)
      ? diffMin(record.lunch_out, record.lunch_in)
      : 0;
    return Math.max(0, Math.round((ref - new Date(record.clock_in)) / 60000) - (lunchDur || 0));
  };

  return (
    <div className="page-card" style={{ maxWidth: 560, margin: '0 auto' }}>
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Bater Ponto</div>
          <div className="page-card-subtitle">Registre os horários do seu dia de trabalho</div>
        </div>
        <LuClock size={28} color="var(--accent)" />
      </div>

      <Clock />

      {message && (
        <div className={`alert-custom ${message.type}`} style={{ marginBottom: 20, fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
          {message.text}
        </div>
      )}

      {/* Avatar e dados */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%', background: 'var(--surface-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--accent)', fontWeight: 800, fontSize: 28, margin: '0 auto 12px',
        }}>
          {employee.full_name.charAt(0).toUpperCase()}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)' }}>{employee.full_name}</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
          {employee.position || 'Operador'}
          {employee.registration ? ` · Mat. ${employee.registration}` : ''}
        </div>
        {employee.department && (
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 }}>
            {employee.department.name}
          </div>
        )}
      </div>

      {checking && (
        <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 13, marginBottom: 20 }}>
          Verificando registros…
        </div>
      )}

      {!checking && (
        <>
          {/* Linha do tempo das batidas */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 24 }}>
            {['clock_in', 'lunch_out', 'lunch_in', 'clock_out'].map((s, i) => {
              const done  = record && (
                (s === 'clock_in'  && record.clock_in)  ||
                (s === 'lunch_out' && record.lunch_out)  ||
                (s === 'lunch_in'  && record.lunch_in)   ||
                (s === 'clock_out' && record.clock_out)
              );
              const active = step === s;
              const times  = {
                clock_in:  record?.clock_in,
                lunch_out: record?.lunch_out,
                lunch_in:  record?.lunch_in,
                clock_out: record?.clock_out,
              };
              return (
                <div key={s} style={{
                  background: done ? 'var(--success-soft)' : active ? 'var(--accent-soft)' : 'var(--surface-2)',
                  border: `1px solid ${done ? 'var(--success-border)' : active ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8, padding: '10px 8px', textAlign: 'center',
                }}>
                  <div translate="no" style={{ fontSize: 10, fontWeight: 700, color: done ? 'var(--success)' : active ? 'var(--accent)' : 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    {['Entrada', 'Saída Alm.', 'Retorno', 'Saída Geral'][i]}
                  </div>
                  <div translate="no" style={{ fontSize: 15, fontWeight: 800, color: done ? 'var(--success)' : 'var(--text-3)' }}>
                    {times[s] ? fmt(times[s]) : '—:——'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Horas trabalhadas (parcial ou total) */}
          {record?.clock_in && (
            <div translate="no" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 24, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                  {step === 'done' ? 'Horas Trabalhadas' : 'Trabalhado até agora'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)' }}>
                  {fmtMin(workedNow())}
                </div>
              </div>
              {record.lunch_out && record.lunch_in && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                    Intervalo Almoço
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)' }}>
                    {fmtMin(diffMin(record.lunch_out, record.lunch_in))}
                  </div>
                </div>
              )}
              {step === 'done' && workedNow() > 480 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                    Horas Extras
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--danger)' }}>
                    {fmtMin(workedNow() - 480)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botão de ação */}
          {step === 'done' ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--success)', fontWeight: 700, fontSize: 15 }}>
                <LuCheckCircle2 size={20} />
                Jornada encerrada para hoje
              </div>
              {workedNow() > 480 && (
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                  Horas extras serão computadas pela gestão (CLT art. 59)
                </div>
              )}
            </div>

          ) : step === 'clock_out' && !confirmingOut ? (
            /* Saída geral: exige confirmação explícita antes de registrar */
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => setConfirmingOut(true)}
                style={{
                  padding: '14px 44px', border: 'none', borderRadius: 10,
                  cursor: 'pointer', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
                  background: cfg.bg, color: cfg.color,
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                }}
              >
                {cfg.icon}
                {cfg.label}
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>
                Passo 4 de 4 · {STEP_LABEL['clock_out']}
              </div>
            </div>

          ) : step === 'clock_out' && confirmingOut ? (
            /* Confirmação de saída */
            <div style={{
              background: 'var(--danger-soft)', border: '1px solid var(--danger-border)',
              borderRadius: 12, padding: '20px 24px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
                Confirmar Saída Geral?
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
                Ao confirmar, sua jornada de hoje será encerrada.<br />
                Esta ação não pode ser desfeita pelo operador.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  onClick={() => setConfirmingOut(false)}
                  disabled={loading}
                  style={{
                    padding: '11px 24px', border: '1px solid var(--border)', borderRadius: 9,
                    background: 'var(--surface-1)', cursor: 'pointer', fontSize: 14,
                    fontWeight: 600, fontFamily: 'inherit', color: 'var(--text-2)',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleClock}
                  disabled={loading}
                  style={{
                    padding: '11px 28px', border: 'none', borderRadius: 9,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                    background: 'var(--danger)', color: 'var(--on-solid)',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  <LuLogOut size={16} />
                  {loading ? 'Registrando…' : 'Sim, registrar saída'}
                </button>
              </div>
            </div>

          ) : (
            /* Demais passos: entrada, saída almoço, retorno */
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleClock}
                disabled={loading}
                style={{
                  padding: '14px 44px', border: 'none', borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
                  background: cfg.bg, color: cfg.color,
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  opacity: loading ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {cfg.icon}
                {loading ? 'Registrando…' : cfg.label}
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>
                Passo {['clock_in','lunch_out','lunch_in','clock_out'].indexOf(step) + 1} de 4
                {' · '}{STEP_LABEL[step]}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
